import { pageShare, fmtPages } from '@generated/components/pages';
import { useEffect, useState } from 'react';
import { friendlyError } from '@generated/hooks/friendlyError';
import { Loader2, Plus, Repeat, Archive } from 'lucide-react';
import { getStaffPass } from '@generated/hooks/staffPass';
import { getAdList, updateAdField, repeatLastMonth, importOldOctober, deleteAd } from '@generated/server/adlist';
import { money } from '@generated/components/pricing';
import { AdForm } from '@generated/components/AdForm';

type Res = Awaited<ReturnType<typeof getAdList>>;
type Ad = Res['items'][number];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const COPY = ['Need Copy', 'Have Copy', 'Same as Last Month'] as const;
const BILLED = ['Not Billed', 'Invoiced', 'Paid'] as const;
const SIZES = ['Full Page', 'Half Page', 'Quarter Page', 'Fifth Page', 'Eighth Page'] as const;
const SECTIONS = ['ROP', 'Arts', 'Special Section'] as const;
const SIZE_ORDER = ['Full Page', 'Half Page', 'Quarter Page', 'Fifth Page', 'Eighth Page', 'Business Card'] as const;


function monthOptions() {
  const now = new Date();
  return Array.from({ length: 18 }, (_, i) => { const d = new Date(now.getFullYear(), now.getMonth() - 3 + i, 1); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; });
}

export function AdList() {
  const opts = monthOptions();
  const [month, setMonth] = useState(opts[4] ?? opts[0] ?? '');
  const [paper, setPaper] = useState<'Red Hook Star-Revue' | 'The Village Star-Revue'>('Red Hook Star-Revue');
  const [items, setItems] = useState<Ad[]>([]);
  const [totals, setTotals] = useState<Res['totals']>([]);
  const [, setCopyCounts] = useState<Res["copy"]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [repeating, setRepeating] = useState(false);
  const [editing, setEditing] = useState<Ad | 'new' | null>(null);
  const [tick, setTick] = useState(0);
  const [importing, setImporting] = useState(false);
  const [armed, setArmed] = useState<string | null>(null);
  const remove = async (ad: Ad) => {
    if (armed !== ad.id) { setArmed(ad.id); setTimeout(() => setArmed((a) => (a === ad.id ? null : a)), 4000); return; }
    setArmed(null); const prev = items; setItems((p) => p.filter((x) => x.id !== ad.id));
    try { await deleteAd({ data: { pass: getStaffPass(), id: ad.id } }); setNotice(`Removed ${ad.name} from ${month}.`); setTick((t) => t + 1); }
    catch (e) { console.error(e); setItems(prev); setError('Could not delete that ad.'); }
  };
  const runImport = async () => {
    setImporting(true); setNotice(null); setError(null);
    try { const r = await importOldOctober({ data: { pass: getStaffPass() } }); setNotice(`Brought over ${r.added} ad(s) from your old October 2026, Red Hook board${r.skipped ? ` (${r.skipped} already here, skipped)` : ''}.`); setTick((t) => t + 1); }
    catch (e) { console.error(e); setError('Could not bring over the old October list. Please try again.'); }
    finally { setImporting(false); }
  };

  useEffect(() => {
    let active = true;
    setRefetching(true); setError(null);
    getAdList({ data: { pass: getStaffPass(), month, paper } })
      .then((r) => { if (!active) return; setItems(r?.items ?? []); setTotals(r?.totals ?? []); setCopyCounts(r?.copy ?? []); setCursor(r?.cursor ?? null); })
      .catch((e) => { console.error(e); if (active) setError(friendlyError(e, 'Could not load the ad list.')); })
      .finally(() => { if (active) { setLoading(false); setRefetching(false); } });
    return () => { active = false; };
  }, [month, paper, tick]);

  const more = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try { const r = await getAdList({ data: { pass: getStaffPass(), month, paper, cursor } }); setItems((p) => [...p, ...(r?.items ?? [])]); setCursor(r?.cursor ?? null); }
    catch (e) { console.error(e); setError('Could not load more ads.'); }
    finally { setLoadingMore(false); }
  };

  const setField = async (ad: Ad, f: { adCopy?: (typeof COPY)[number]; billed?: (typeof BILLED)[number]; adSize?: string; cost?: number; section?: (typeof SECTIONS)[number] }) => {
    const prev = items;
    setItems((p) => p.map((x) => (x.id === ad.id ? { ...x, ...f } : x)));
    try { await updateAdField({ data: { pass: getStaffPass(), id: ad.id, ...f } }); }
    catch (e) { console.error(e); setItems(prev); setError('Could not save that change.'); return; }
    if (f.cost !== undefined) setTick((t) => t + 1);
  };

  const repeat = async () => {
    setRepeating(true); setNotice(null); setError(null);
    try { const r = await repeatLastMonth({ data: { pass: getStaffPass(), month, paper } }); setNotice(r.added ? `Added ${r.added} ${paper} ad(s) from ${r.from}, marked "Same as Last Month".` : `${paper} had no ads in ${r.from} to bring over.`); setTick((t) => t + 1); }
    catch (e) { console.error(e); setError('Could not copy last month\'s ads.'); }
    finally { setRepeating(false); }
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-[family-name:var(--font-heading)] text-3xl font-semibold">Ad List</h2>
          <p className="text-sm text-muted-foreground">Display ads for each issue. Classifieds are kept separately.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select aria-label="Issue month" value={month} onChange={(e) => setMonth(e.target.value)} className="min-h-11 border border-foreground bg-card px-3 text-sm font-semibold">
            {opts.map((m) => <option key={m}>{m}</option>)}
          </select>
          <button onClick={() => void repeat()} disabled={repeating} className="inline-flex min-h-11 items-center gap-1.5 border border-foreground px-3 text-sm disabled:opacity-60">
            {repeating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat className="h-4 w-4" />}Repeat last month's ads
          </button>
          <button onClick={() => setEditing('new')} className="inline-flex min-h-11 items-center gap-1.5 bg-primary px-4 text-sm font-semibold text-primary-foreground"><Plus className="h-4 w-4" />Add ad</button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {(["Red Hook Star-Revue", "The Village Star-Revue"] as const).map((p) => { const t = totals.find((x) => x.paper === p); const on = paper === p; return (
          <button key={p} type="button" onClick={() => setPaper(p)} aria-pressed={on}
            className={`border-2 p-4 text-left text-primary-foreground transition-all ${p === "Red Hook Star-Revue" ? "bg-primary" : "bg-[hsl(var(--brand-blue))]"} ${on ? "border-foreground shadow-[5px_5px_0_hsl(var(--foreground))]" : "border-transparent opacity-60 hover:opacity-90"}`}>
            <span className="block font-[family-name:var(--font-heading)] text-xl font-semibold">{p}</span>
            <span className="block text-sm opacity-90">{on ? `${month} · ${t?.count ?? 0} ads · ${money(t?.total ?? 0)}` : "Show this paper's ads"}</span>
          </button>); })}
      </div>

      {month === 'October 2026' && (
        <div className="flex flex-wrap items-center gap-3 border border-dashed border-foreground bg-card p-3 text-sm">
          <span>Bring over the ads from your old <strong>October 2026, Red Hook</strong> board.</span>
          <button onClick={() => void runImport()} disabled={importing} className="inline-flex min-h-11 items-center gap-2 border border-foreground px-4 font-semibold disabled:opacity-60">{importing && <Loader2 className="h-4 w-4 animate-spin" />}Import old October list</button>
        </div>
      )}
      {notice && <p className="text-sm">{notice}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className={`overflow-x-auto border border-border bg-card ${refetching && !loading ? 'opacity-60' : ''}`}>
        <table className="w-full min-w-[820px] text-sm">
          <thead className="border-b-2 border-foreground text-left text-[11px] uppercase tracking-wider">
            <tr>{['Advertiser', 'Paper', 'Size', 'Section', 'Cost', 'Ad copy', 'Billed', ''].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && Array.from({ length: 4 }).map((_, i) => <tr key={i}><td colSpan={8} className="p-3"><div className="h-4 animate-pulse bg-muted" /></td></tr>)}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No ads for {month} yet. Add one, repeat last month's, or copy orders from the Orders page.</td></tr>}
            {items.map((a) => (
              <tr key={a.id}>
                <td className="px-3 py-2 font-medium">{a.name}{a.portalOrderId ? <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">portal</span> : null}</td>
                <td className="px-3 py-2">{a.paper === 'The Village Star-Revue' ? 'Village' : a.paper ? 'Red Hook' : '—'}</td>
                <td className="px-3 py-2"><select aria-label="Ad size" value={a.adSize ?? ''} onChange={(e) => void setField(a, { adSize: e.target.value })} className="min-h-9 border border-border bg-background px-1 text-xs"><option value="">—</option>{[...new Set([...SIZES, ...(a.adSize ? [a.adSize] : [])])].map((s) => <option key={s}>{s}</option>)}</select></td>
                <td className="px-3 py-2"><select aria-label="Section" value={a.section ?? ''} onChange={(e) => void setField(a, { section: e.target.value as (typeof SECTIONS)[number] })} className="min-h-9 border border-border bg-background px-1 text-xs"><option value="" disabled>—</option>{SECTIONS.map((s) => <option key={s}>{s}</option>)}</select>{a.section === 'Special Section' && a.specialSectionName ? <div className="mt-0.5 text-[10px] text-muted-foreground">{a.specialSectionName}</div> : null}</td>
                <td className="px-3 py-2"><CostInput value={a.cost ?? 0} onSave={(v) => void setField(a, { cost: v })} /></td>
                <td className="px-3 py-2"><select aria-label="Ad copy" value={a.adCopy ?? ''} onChange={(e) => void setField(a, { adCopy: e.target.value as (typeof COPY)[number] })} className={`min-h-9 border bg-background px-1 text-xs ${a.adCopy === 'Need Copy' || !a.adCopy ? 'border-destructive text-destructive' : 'border-border'}`}><option value="" disabled>—</option>{COPY.map((c) => <option key={c}>{c}</option>)}</select></td>
                <td className="px-3 py-2"><select aria-label="Billed" value={a.billed ?? ''} onChange={(e) => void setField(a, { billed: e.target.value as (typeof BILLED)[number] })} className="min-h-9 border border-border bg-background px-1 text-xs"><option value="" disabled>—</option>{BILLED.map((c) => <option key={c}>{c}</option>)}</select></td>
                <td className="px-3 py-2"><div className="flex items-center gap-3"><button onClick={() => setEditing(a)} className="min-h-9 text-xs underline underline-offset-2">Edit</button>
                  <button onClick={() => void remove(a)} aria-label={`Delete ${a.name}`} className={`min-h-9 px-2 text-xs font-semibold ${armed === a.id ? "bg-destructive text-primary-foreground" : "text-destructive underline underline-offset-2"}`}>{armed === a.id ? "Press again to delete" : "Delete"}</button></div></td>
              </tr>
            ))}
          </tbody>
          {!loading && items.length > 0 && (() => {
            const t = totals.find((x) => x.paper === paper);
            const pages = items.reduce((s, a) => s + pageShare(a.adSize), 0);
            const bySize = SIZE_ORDER.map((s) => [s, items.filter((a) => (a.adSize ?? '').toLowerCase() === s.toLowerCase()).length] as const).filter(([, n]) => n > 0);
            const other = items.length - bySize.reduce((s, [, n]) => s + n, 0);
            return (
              <tfoot>
                <tr className="border-t-2 border-foreground bg-[hsl(var(--brand-blue)/0.14)] font-bold">
                  <td className="px-3 py-3 text-base" colSpan={2}>Total · {t?.count ?? items.length} ads</td>
                  <td className="px-3 py-3" colSpan={2}>
                    <span className="block text-base">{fmtPages(pages)} {pages === 1 ? 'page' : 'pages'}</span>
                    <span className="block text-[11px] font-semibold">{bySize.map(([s, n]) => `${n} × ${s}`).join(' · ')}{other ? ` · ${other} other` : ''}</span>
                  </td>
                  <td className="px-3 py-3 text-base">{money(t?.total ?? items.reduce((s, a) => s + (a.cost ?? 0), 0))}</td>
                  <td colSpan={3} className="px-3 py-3">
                    <span className="block text-base">{pages > 0 ? `${money(items.reduce((s, a) => s + (a.cost ?? 0), 0) / pages)} per page` : '—'}</span>
                    <span className="block text-[11px] font-semibold">Average income per ad page{cursor ? ' · covers ads shown; load more to include the rest' : ''}</span>
                  </td>
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </div>
      {cursor && <button onClick={() => void more()} disabled={loadingMore} className="mx-auto flex min-h-11 items-center gap-2 border border-foreground px-5 text-sm font-semibold disabled:opacity-60">{loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}Load more ads</button>}

      <p className="flex items-start gap-2 text-xs text-muted-foreground"><Archive className="mt-0.5 h-3.5 w-3.5 shrink-0" />Housekeeping: on the Ad List board, archive ads for issues more than three months old (filter by Issue Month, select, Archive).</p>

      {editing && <AdForm month={month} paper={paper} ad={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setTick((t) => t + 1); }} />}
    </div>
  );
}


function CostInput({ value, onSave }: { value: number; onSave: (v: number) => void }) {
  const [v, setV] = useState(String(value));
  useEffect(() => setV(String(value)), [value]);
  const commit = () => { const n = Math.max(0, Number(v.replace(/[$,]/g, '')) || 0); if (n !== value) onSave(n); else setV(String(value)); };
  return (
    <span className="inline-flex items-center border border-border bg-background">
      <span className="pl-2 text-xs text-muted-foreground">$</span>
      <input aria-label="Cost" inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} className="h-9 w-20 bg-transparent px-1 text-xs tabular-nums outline-none" />
    </span>
  );
}
